import type { CommandInput, CommandName, FieldValue, Node } from "@melo/shared";
import { rankBetween } from "@melo/shared/client";
import { api, ApiError } from "./api";
import { store, type Patch } from "./store";
import { toast } from "./toast";
import { keyOf, statusOf } from "./select";

/**
 * Sends a command. Optimistic patches show the result at once; they are dropped when the
 * server answers, by which time its events have replaced them with the real state.
 */
export async function send<N extends CommandName>(type: N, input: CommandInput<N>, patches: Patch[] = [], opts: { quiet?: boolean } = {}): Promise<unknown> {
  const clientId = crypto.randomUUID();
  store.addPending(clientId, patches);
  try {
    const res = await api.command(type, input, clientId);
    store.applyEvents(res.events);
    return res.result;
  } catch (e) {
    if (!opts.quiet) toast(e instanceof ApiError ? e.message : "Something went wrong.", { tone: "error" });
    throw e;
  } finally {
    store.dropPending(clientId);
  }
}

/** Same rule as the server: right after `afterId`, before whatever follows it in the whole space. */
export function rankFor(spaceId: string, place: { afterId?: string | null; beforeId?: string | null }, movingId?: string): string {
  const ranks = [...store.t.nodes.values()]
    .filter((n) => n.spaceId === spaceId && n.id !== movingId)
    .map((n) => n.rank)
    .sort();
  if (place.afterId) {
    const a = store.t.nodes.get(place.afterId)!.rank;
    return rankBetween(a, ranks.find((r) => r > a) ?? null);
  }
  if (place.beforeId) {
    const b = store.t.nodes.get(place.beforeId)!.rank;
    return rankBetween([...ranks].reverse().find((r) => r < b) ?? null, b);
  }
  return rankBetween(ranks.at(-1) ?? null, null);
}

export interface MoveChange {
  afterId?: string | null;
  beforeId?: string | null;
  parentId?: string | null;
  status?: string;
  fields?: Record<string, FieldValue>;
  assignees?: { actorId: string; role: "owner" | "reviewer" }[];
  labels?: string[];
}

/** Moves a node with an instant local result and an Undo in the toast. */
export async function moveNode(node: Node, change: MoveChange, label?: string) {
  const t = store.t;
  const rank = change.afterId || change.beforeId ? rankFor(node.spaceId, change, node.id) : node.rank;
  const next: Node = {
    ...node,
    rank,
    status: change.status ?? node.status,
    parentId: change.parentId === undefined ? node.parentId : change.parentId,
    fields: change.fields ? mergeFields(node.fields, change.fields) : node.fields,
    assignees: change.assignees ?? node.assignees,
    labels: change.labels ?? node.labels,
  };
  // Where the node was, so Undo can put it back.
  const prev = [...t.nodes.values()].filter((n) => n.spaceId === node.spaceId && n.id !== node.id && n.rank < node.rank).sort((a, b) => (a.rank < b.rank ? 1 : -1))[0];
  const before = { status: node.status, parentId: node.parentId, fields: node.fields, assignees: node.assignees, labels: node.labels, afterId: prev?.id ?? null };
  await send(
    "node.move",
    {
      nodeId: node.id,
      ...(change.afterId ? { afterId: change.afterId } : change.beforeId ? { beforeId: change.beforeId } : {}),
      ...(change.parentId !== undefined ? { parentId: change.parentId } : {}),
      ...(change.status ? { status: change.status } : {}),
      ...(change.fields ? { fields: change.fields } : {}),
      ...(change.assignees ? { assignees: change.assignees } : {}),
      ...(change.labels ? { labels: change.labels } : {}),
    },
    [{ table: "nodes", id: node.id, value: next }],
  );
  if (label) {
    toast(`${label}`, {
      action: {
        label: "Undo",
        run: () => {
          const cur = store.t.nodes.get(node.id);
          if (!cur) return;
          const restoreFields = change.fields ? Object.fromEntries(Object.keys(change.fields).map((k) => [k, before.fields[k] ?? null])) : undefined;
          void moveNode(cur, {
            ...(before.afterId && store.t.nodes.get(before.afterId) ? { afterId: before.afterId } : { beforeId: firstInSpace(node.spaceId, node.id) }),
            ...(change.status ? { status: before.status } : {}),
            ...(change.parentId !== undefined ? { parentId: before.parentId } : {}),
            ...(restoreFields ? { fields: restoreFields } : {}),
            ...(change.assignees ? { assignees: before.assignees } : {}),
            ...(change.labels ? { labels: before.labels } : {}),
          });
        },
      },
    });
  }
}

function firstInSpace(spaceId: string, except: string): string | null {
  return [...store.t.nodes.values()].filter((n) => n.spaceId === spaceId && n.id !== except).sort((a, b) => (a.rank < b.rank ? -1 : 1))[0]?.id ?? null;
}

function mergeFields(cur: Record<string, FieldValue>, patch: Record<string, FieldValue>) {
  const out = { ...cur };
  for (const [k, v] of Object.entries(patch)) {
    if (v === null) delete out[k];
    else out[k] = v;
  }
  return out;
}

/** Updates simple properties with an instant local result. */
export async function updateNode(node: Node, patch: Omit<CommandInput<"node.update">, "nodeId">) {
  const next: Node = {
    ...node,
    ...(patch.title !== undefined ? { title: patch.title } : {}),
    ...(patch.body !== undefined ? { body: patch.body } : {}),
    ...(patch.status !== undefined ? { status: patch.status } : {}),
    ...(patch.startDate !== undefined ? { startDate: patch.startDate } : {}),
    ...(patch.dueDate !== undefined ? { dueDate: patch.dueDate } : {}),
    ...(patch.coverRefId !== undefined ? { coverRefId: patch.coverRefId } : {}),
    fields: patch.fields ? mergeFields(node.fields, patch.fields) : node.fields,
  };
  return send("node.update", { nodeId: node.id, ...patch }, [{ table: "nodes", id: node.id, value: next }]);
}

export async function setStatus(node: Node, status: string) {
  const kind = store.t.kinds.get(node.kindId);
  const label = statusOf(kind, status)?.label ?? status;
  await moveNode(node, { status }, `${keyOf(store.t, node)} moved to ${label}`);
}

export async function archiveNode(node: Node) {
  const key = keyOf(store.t, node);
  await send("node.archive", { nodeId: node.id, archived: true }, [{ table: "nodes", id: node.id, value: { ...node, archivedAt: new Date().toISOString() } }]);
  toast(`Archived ${key}`, { action: { label: "Undo", run: () => void send("node.archive", { nodeId: node.id, archived: false }) } });
}

export async function assign(node: Node, list: string[] | { actorId: string; role: "owner" | "reviewer" }[]) {
  const assignees = list.map((x) => (typeof x === "string" ? { actorId: x, role: "owner" as const } : x));
  await send("node.assign", { nodeId: node.id, assignees }, [{ table: "nodes", id: node.id, value: { ...node, assignees } }]);
}

export interface BulkChange {
  status?: string;
  /** Replaces owners; reviewers stay. */
  owners?: string[];
  addLabels?: string[];
  removeLabels?: string[];
  fields?: Record<string, FieldValue>;
  dueDate?: string | null;
  parentId?: string | null;
  archived?: boolean;
}

/** One change to many items at once, shown immediately, with an Undo that puts every item back as it was. */
export async function bulkEdit(ids: string[], change: BulkChange, label: string) {
  const t = store.t;
  const nodes = ids.map((id) => t.nodes.get(id)).filter((n): n is Node => Boolean(n));
  const patches = nodes.map((n) => {
    const kind = t.kinds.get(n.kindId);
    const next: Node = {
      ...n,
      status: change.status && kind?.statuses.some((s) => s.key === change.status) ? change.status : n.status,
      assignees: change.owners ? [...change.owners.map((actorId) => ({ actorId, role: "owner" as const })), ...n.assignees.filter((x) => x.role === "reviewer" && !change.owners!.includes(x.actorId))] : n.assignees,
      labels: change.addLabels || change.removeLabels ? [...new Set([...n.labels, ...(change.addLabels ?? [])])].filter((l) => !(change.removeLabels ?? []).includes(l)) : n.labels,
      fields: change.fields ? mergeFields(n.fields, Object.fromEntries(Object.entries(change.fields).filter(([k]) => kind?.fields.some((f) => f.key === k)))) : n.fields,
      dueDate: change.dueDate !== undefined ? change.dueDate : n.dueDate,
      parentId: change.parentId !== undefined ? change.parentId : n.parentId,
      archivedAt: change.archived ? new Date().toISOString() : n.archivedAt,
    };
    return { table: "nodes" as const, id: n.id, value: next };
  });
  const before = nodes.map((n) => ({ ...n }));
  await send("node.bulk", { nodeIds: nodes.map((n) => n.id), ...change }, patches);
  toast(label, {
    action: {
      label: "Undo",
      run: () => {
        if (change.archived) return void send("node.bulk", { nodeIds: before.map((n) => n.id), archived: false });
        for (const n of before) {
          const cur = store.t.nodes.get(n.id);
          if (!cur) continue;
          if (change.owners) void send("node.assign", { nodeId: n.id, assignees: n.assignees });
          const fieldsBack = change.fields ? Object.fromEntries(Object.keys(change.fields).filter((k) => k in cur.fields || k in n.fields).map((k) => [k, n.fields[k] ?? null])) : undefined;
          void send("node.move", {
            nodeId: n.id,
            ...(change.status && cur.status !== n.status ? { status: n.status } : {}),
            ...(change.parentId !== undefined ? { parentId: n.parentId } : {}),
            ...(change.addLabels || change.removeLabels ? { labels: n.labels } : {}),
            ...(fieldsBack && Object.keys(fieldsBack).length ? { fields: fieldsBack } : {}),
          });
          if (change.dueDate !== undefined) void send("node.update", { nodeId: n.id, dueDate: n.dueDate });
        }
      },
    },
  });
}

type ChecklistOp = CommandInput<"node.checklist">;

/** One checklist change, shown at once. The server applies it to the current list, so concurrent ticks both land. */
export function checklist(node: Node, op: ChecklistOp) {
  const list = [...node.checklist];
  let input = op;
  if (op.op === "add") {
    const item = { id: op.id ?? crypto.randomUUID(), text: op.text, done: false };
    const after = op.afterId ? list.findIndex((c) => c.id === op.afterId) : -1;
    list.splice(after >= 0 ? after + 1 : list.length, 0, item);
    input = { ...op, id: item.id };
  } else {
    const at = list.findIndex((c) => c.id === op.itemId);
    if (at < 0) return Promise.resolve();
    if (op.op === "set") list[at] = { ...list[at]!, ...(op.text !== undefined ? { text: op.text } : {}), ...(op.done !== undefined ? { done: op.done } : {}) };
    else if (op.op === "remove") list.splice(at, 1);
    else {
      const [item] = list.splice(at, 1);
      list.splice(Math.min(op.index, list.length), 0, item!);
    }
  }
  return send("node.checklist", input, [{ table: "nodes", id: node.id, value: { ...node, checklist: list } }]);
}
